import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InsightsIcon from '@mui/icons-material/Insights';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import { Link, Switch, Tooltip, Typography } from '@mui/material';
import React from 'react';

import DeleteIconButton from '../components/DeleteIconButton';
import WhiteBackgroundTooltip from '../components/WhiteTooltip';
import { ContextStore } from '../store/contextStore';
import { CAMPAIGN_STATUS } from './campaignStorage';
import { DELIVERY_METHODS, TOUR_OVERRIDE_MODES } from './utils';
import { BOOKING_STATES } from '../trips/UserManager/utils';

const deliveryMethodSeq = (d) => (d.sequence != null ? d.sequence : d.seq) ?? 0;

const compareDeliveryMethods = (a, b) => deliveryMethodSeq(a) - deliveryMethodSeq(b);

export const DeliveryByDataGridCol = (props) => {
    const labelContainerStyle = {
        display: 'flex',
        gap: '0.25rem',
        backgroundColor: '#F1F1F2',
        borderRadius: '2px',
        padding: '0.15rem 0.4rem',
        alignItems: 'center',
        opacity: 0.85
    };

    const sequenceStyle = {
        backgroundColor: '#D9D9D9',
        padding: '0 0.25rem',
        borderRadius: '50%',
        margin: '0.25rem 0'
    };

    // Every method is shown, wrapping onto more lines (CP-8474): they used to stop at two
    // plus "...", and a notification can have up to 12. The grid's rows grow to fit.
    const sorted = [...(props.row.delivery_methods || [])].sort(compareDeliveryMethods);

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', minWidth: 0, padding: '0.5rem 0' }}>
            {sorted.map((db, idx) => {
                return (
                    <div key={`${db.method}-${deliveryMethodSeq(db)}-${idx}`} style={labelContainerStyle} >
                        <div>
                            <Typography>{DELIVERY_METHODS[db.method]}</Typography>
                        </div>
                        <div style={sequenceStyle} >
                            {<Typography>{deliveryMethodSeq(db)}</Typography>}</div>
                    </div>
                );
            })}
        </div>
    );

}

export const ToursDataGridCol = (props) => {
    const tripLabelStyle = {
        backgroundColor: 'rgba(255, 152, 0, 0.2)',
        padding: '6px 16px',
        borderRadius: '16px',
        overflowWrap: 'anywhere'
    };

    const threeDotsStyle = {
        padding: '6px 16px',
        borderRadius: '16px'
    };

    let allowed_to_show_tours = [];
    if (props.row.tours.length === 0) {
        return <div style={tripLabelStyle}>Default (All)</div>;
    }
    if (props.row.tours.length > 2) {
        allowed_to_show_tours = [props.row.tours[0], props.row.tours[1]];
    } else {
        allowed_to_show_tours = [...props.row.tours];

    }

    return (
        <WhiteBackgroundTooltip
            title={props.row.tours.map((tour, idx) => {
                return <div key={idx} style={tripLabelStyle}>{tour.name}</div>;
            })} >
            {/* Wraps: the Notifications grid's rows grow with their content (CP-8474) */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', minWidth: 0, padding: '0.5rem 0' }}>
                {allowed_to_show_tours.map((tour, idx) => {
                    return <div key={idx} style={tripLabelStyle} >{tour.name}</div>;
                })}
                {props.row.tours.length > 2 && <div style={threeDotsStyle} >...</div>}
            </div>
        </WhiteBackgroundTooltip>
    );
}


const RenderRoleNames = ({ allowed_roles_to_display, roleMap, limit }) => {
    return <> {allowed_roles_to_display.map((role, index) => {
        let getBgColor = (role) => {
            //Role is null means Guest
            if (!role) {
                return '#FFD2D1';
            }
            if (roleMap[role].name === 'Admin') {
                return '#D8D8D8';
            }
            if (roleMap[role].name === 'Tour guide') {
                return '#E1CFFF';
            }
            if (roleMap[role].name === 'Support Observer') {
                return '#DAF3E5';
            }
        };

        let backgroundColor = getBgColor(role);
        return (
            <div key={index} style={{ backgroundColor: backgroundColor, borderRadius: '2px', padding: '0.15rem 0.4rem', opacity: 0.85 }}>
                <Typography>
                    {!role && 'Guest'}
                    {role === 0 && 'Observer'}
                    {roleMap[role]?.name && roleMap[role].name}
                </Typography>
            </div>
        );
    })}</>

}

export const RolesDataGridCol = (props, roleMap) => {
    let allowed_roles_to_display = [];

    if (props.row.apply_to_guests) {
        allowed_roles_to_display.push(null);
    }

    allowed_roles_to_display = [...allowed_roles_to_display, ...props.row.roles];

    // All of them, wrapping (CP-8474): the column is narrow, and a "..." meant widening it.
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', minWidth: 0, padding: '0.5rem 0' }}>
            <RenderRoleNames allowed_roles_to_display={allowed_roles_to_display} roleMap={roleMap} />
        </div>
    );
};


export const BookingStateDataGridCol = (props) => {
    const bookingStateValue = props.row.booking_state;
    
    if (bookingStateValue === null || bookingStateValue === undefined) {
        return <Typography color="textSecondary">-</Typography>;
    }

    const getBgColor = (state) => {
        switch (state) {
            case 1:
                return '#D4EDDA';
            case 0:
                return '#FFF3CD';
            case 2:
            case 3:
                return '#F8D7DA';
            default:
                return '#F1F1F2';
        }
    };

    return (
        <div style={{ backgroundColor: getBgColor(bookingStateValue), borderRadius: '2px', padding: '0.15rem 0.4rem', opacity: 0.85 }}>
            <Typography>{BOOKING_STATES[bookingStateValue] || '-'}</Typography>
        </div>
    );
};

export const TourOverrideModeDataGridCol = (props) => {
    const tourOverrideMode = props.row.tour_override_mode;
    
    if (tourOverrideMode === null || tourOverrideMode === undefined) {
        return <Typography color="textSecondary">-</Typography>;
    }

    const getBgColor = (mode) => {
        switch (mode) {
            case 0:
                return '#E3F2FD';
            case 1:
                return '#FFF9C4';
            case 2:
                return '#F3E5F5';
            default:
                return '#F1F1F2';
        }
    };

    const getLabel = (mode) => {
        switch (mode) {
            case 0:
                return 'Non-overridable';
            case 1:
                return 'Overridable';
            case 2:
                return 'Tour Specific';
            default:
                return '-';
        }
    };

    return (
        <WhiteBackgroundTooltip title={TOUR_OVERRIDE_MODES[tourOverrideMode] || '-'}>
            <div style={{ backgroundColor: getBgColor(tourOverrideMode), borderRadius: '2px', padding: '0.15rem 0.4rem', opacity: 0.85 }}>
                <Typography>{getLabel(tourOverrideMode)}</Typography>
            </div>
        </WhiteBackgroundTooltip>
    );
};

export const ActionsColumn = (p, handleDuplicate, onDeleteNotification, onSwitchChange, history) => {
    const { hasPerm } = React.useContext(ContextStore);

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}>
            <Tooltip title={hasPerm('notification', 'write') ? 'Edit' : hasPerm('notification', 'read') ? 'View' : ''}>
                <Link
                    href={`/notifications/${p.row.id}`}
                    onClick={(e) => {
                        if (!hasPerm('notification', 'read')) {
                            return;
                        }

                        e.preventDefault();
                        history.push(`/notifications/${p.row.id}`);
                    }}
                >
                    {hasPerm('notification', 'write') ? <EditIcon color="primary" fontSize="small" /> : hasPerm('notification', 'read') ? <VisibilityIcon color="primary" /> : null}
                </Link>
            </Tooltip>
            {hasPerm('notification', 'write') && (
                <Tooltip title="Duplicate">
                    <div onClick={() => handleDuplicate(p?.row)}>
                        <ContentCopyIcon color="primary"></ContentCopyIcon>
                    </div>
                </Tooltip>
            )}
            <DeleteIconButton perm="notification.delete" sx={{ p: 0 }} onClick={() => onDeleteNotification(p?.row)} />
            <Tooltip title={p?.row?.enabled ? 'Disable' : 'Enable'}>
                <div>
                    <Switch
                        checked={p?.row?.enabled}
                        disabled={!hasPerm('notification', 'write')}
                        onChange={(e) => {
                            e.preventDefault();
                            onSwitchChange(p?.row);
                        }}
                        name="enabled"
                        color="primary"
                    />
                </div>
            </Tooltip>
        </div>
    );
};

/** Placeholder campaigns list — same control layout as {@link ActionsColumn}; navigation/API hooks in later. */
export const CampaignActionsColumn = (p, handlers) => {
    const { onDuplicate, onDelete } = handlers;
    const { hasPerm } = React.useContext(ContextStore);
    const row = p?.row;
    if (!row) {
        return null;
    }

    // Analytics only exists once a campaign has been sent (or is mid-send),
    // mirroring the "Campaign Analytics" step that appears in CampaignForm
    // for those same statuses.
    const canViewAnalytics =
        hasPerm('campaign', 'read') &&
        (row.status === CAMPAIGN_STATUS.SENT || row.status === CAMPAIGN_STATUS.ONGOING);

    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                width: '100%',
                gap: '0.35rem',
                cursor: 'pointer',
            }}
        >
            {canViewAnalytics && (
                <Tooltip title="Campaign Analytics (opens in new tab)">
                    <Link
                        href={`/notifications/campaigns/${row.id}?step=analytics`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="none"
                        sx={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                        <InsightsIcon color="primary" />
                    </Link>
                </Tooltip>
            )}
            {hasPerm('campaign', 'read') && (
                <Tooltip title="Edit (opens in new tab)">
                    <Link
                        href={`/notifications/campaigns/${row.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="none"
                        sx={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                        <EditIcon color="primary" fontSize="small" />
                    </Link>
                </Tooltip>
            )}
            {hasPerm('campaign', 'write') && (
                <div onClick={() => onDuplicate(row)}>
                    <ContentCopyIcon color="primary" />
                </div>
            )}
            <DeleteIconButton perm="campaign.delete" sx={{ p: 0 }} onClick={() => onDelete(row)} />
        </div>
    );
};
